// 2026 台灣匹克球賽事資料
// 資料來源：中華民國匹克球總會 (CTPF) 公開賽事行事曆（2026-08 版）、TMLP、AEPL
// 最後更新：2026-11-05

export type TournamentLevel = '國際' | '全國' | '區域' | '公益' | '分齡';
export type TournamentStatus = 'upcoming' | 'registration' | 'ongoing' | 'completed';

export interface Tournament {
  id: string;
  name: string;
  nameEn?: string;
  date: string;              // ISO 格式 YYYY-MM-DD
  endDate?: string;
  dateLabel: string;         // 顯示用 e.g. "2026 年 1 月 30 日 - 2 月 1 日"
  venue: string;
  city: string;
  region: string;            // 北部/中部/南部/東部
  level: TournamentLevel;
  status: TournamentStatus;
  organizer: string;
  summary: string;
  categories: string[];      // 組別
  registrationUrl?: string;
  registrationDeadline?: string;
  prizeInfo?: string;
  officialUrl?: string;
  image?: string;
  featured?: boolean;        // 是否為重點賽事
}

const RAW_TOURNAMENTS: Tournament[] = [
  {
    id: 'taiwan-cup-2026',
    name: '2026 臺灣盃全國匹克球公開賽',
    nameEn: 'Taiwan Cup National Pickleball Open 2026',
    date: '2026-01-30',
    endDate: '2026-02-01',
    dateLabel: '2026 年 1 月 30 日 - 2 月 1 日',
    venue: '國立宜蘭大學體育館',
    city: '宜蘭縣',
    region: '東部',
    level: '全國',
    status: 'completed',
    organizer: '中華民國匹克球協會 (CTPF) / 運動部全民運動署補助',
    summary: '由運動部全民運動署補助，台灣最具規模的全國性錦標賽之一。分為國小、國中、高中、大專、教職員與教練公開組等多組別，吸引全台頂尖匹克球選手參與。',
    categories: ['國小組', '國中組', '高中組', '大專組', '教職員組', '教練公開組'],
    registrationDeadline: '2026-01-12',
    officialUrl: 'https://www.ctpf.org.tw/',
    featured: true,
  },
  {
    id: 'napa-cup-2026',
    name: '2026 NAPA 盃全國匹克球錦標賽',
    nameEn: '2026 NAPA Cup National Pickleball Championship',
    date: '2026-02-28',
    endDate: '2026-03-01',
    dateLabel: '2026 年 2 月 28 日 - 3 月 1 日',
    venue: '待公告',
    city: '台灣',
    region: '全國',
    level: '全國',
    status: 'completed',
    organizer: '中華民國匹克球協會 (CTPF)',
    summary: '由中華民國匹克球協會主辦的全國錦標賽，重要的年度積分賽之一，是選手累積排名積分的關鍵賽事。',
    categories: ['公開組', '分齡組', '雙打組'],
    officialUrl: 'https://pickleball.org.tw/activity_2/',
    featured: true,
  },
  {
    id: 'pingtung-invitational-2026',
    name: '115年國民體育日－屏東縣全民匹克球邀請賽',
    nameEn: 'Pingtung County Pickleball Invitational Tournament 2026',
    date: '2026-09-12',
    dateLabel: '2026 年 9 月 12 日',
    venue: '國立屏東大學',
    city: '屏東縣',
    region: '南部',
    level: '區域',
    status: 'completed',
    organizer: '屏東縣匹克球委員會 / 國立屏東大學',
    summary: '原規劃於 3 月的屏東邀請賽併入國民體育日系列，於 9 月 12 日在國立屏東大學舉行。南台灣重要的區域邀請賽，適合各級選手交流切磋。',
    categories: ['公開組', '分齡組'],
    officialUrl: 'https://www.ctpf.org.tw/',
  },
  {
    id: 'gangdu-cup-2026',
    name: '2026 港都盃全國匹克球錦標賽',
    nameEn: 'Gangdu Cup National Pickleball Championships 2026',
    date: '2026-04-18',
    endDate: '2026-04-19',
    dateLabel: '2026 年 4 月 18 - 19 日',
    venue: '樹德科技大學體育館',
    city: '高雄市',
    region: '南部',
    level: '全國',
    status: 'completed',
    organizer: '高雄市匹克球委員會 / 樹德科技大學',
    summary: '南部最具代表性的全國錦標賽之一，已於 4 月 18、19 日圓滿落幕，同場並舉辦 115 年全國身心障礙匹克球觀摩賽。完整成績可於總會官網查詢。',
    categories: ['男子單打', '女子單打', '男子雙打', '女子雙打', '混合雙打', '分齡組'],
    officialUrl: 'https://www.ctpf.org.tw/ctpf/activities/zh-tw/detailed/203',
    featured: true,
  },
  {
    id: 'zhongzheng-cup-2026',
    name: '2026 全國中正盃匹克球錦標賽',
    nameEn: 'National Zhongzheng Cup Pickleball Championships 2026',
    date: '2026-06-18',
    endDate: '2026-06-21',
    dateLabel: '2026 年 6 月 18 - 21 日',
    venue: '台北市大安運動中心 3 樓',
    city: '台北市',
    region: '北部',
    level: '全國',
    status: 'completed',
    organizer: '中華民國匹克球總會',
    summary: '「中正盃」系列錦標賽於大安運動中心舉辦，6 月 18 至 21 日連打四天（6/19 為身心障礙組），並全程官方直播，是北台灣最受矚目的夏季賽事。',
    categories: ['公開組', '長青組', '青少年組', '身心障礙組'],
    officialUrl: 'https://www.ctpf.org.tw/',
    featured: true,
  },
  {
    id: 'aepl-2026',
    name: '2026 AEPL 亞洲菁英匹克球聯盟創始賽季',
    nameEn: 'Asia Elite Pickleball League 2026 Inaugural Season',
    date: '2026-08-29',
    endDate: '2026-11-30',
    dateLabel: '2026 年 8 月 29 日開幕（8 - 11 月，8 站分站賽 + 11 月總決賽）',
    venue: '開幕戰：台中火車站空中廊道（後續巡迴雙北、桃園、新竹、宜蘭、台中、台南、高雄）',
    city: '全台',
    region: '全國',
    level: '全國',
    status: 'ongoing',
    organizer: '亞洲菁英匹克球股份有限公司 (AEPL)',
    summary: '台灣第一個匹克球職業聯賽，2026 年 5 月宣布成立，創始賽季 8 月 29、30 日於台中火車站空中廊道開幕。6 支企業隊參戰（台南旭日雷霆〔尚騰汽車集團與寶嘉聯合共同成立〕、桃園雲豹、Ahhh、蘆沐、富瑞特科技等，橫跨汽車、能源、科技產業），賽季 8 至 11 月朝全台 8 站推進、11 月總決賽，總獎金新台幣 100 萬元。前三站台中（8/30）、高雄（9/20）、宜蘭（10/4）冠軍皆由桃園永豐雲豹拿下，三場冠軍戰都以 3:2 在第 5 回合 Dreambreaker 分出勝負；第 4 站新竹（10/18）由新竹 YANKEY ACE 在主場 3:1 終結雲豹連勝，第 5 站新北（11/1）雲豹 3:1 討回、拿下本季第 4 冠。第 6 站 11/14-15 台北。藝人林志穎擔任賽事大使。',
    categories: ['職業隊際賽'],
    featured: true,
  },
  {
    id: 'jinbi-cup-2026',
    name: '115年度新北金碧盃全國匹克球錦標賽',
    nameEn: 'New Taipei Jinbi Cup National Pickleball Championships 2026',
    date: '2026-08-14',
    endDate: '2026-08-16',
    dateLabel: '2026 年 8 月 14 - 16 日',
    venue: '三峽國民運動中心',
    city: '新北市',
    region: '北部',
    level: '全國',
    status: 'completed',
    organizer: '中華民國匹克球總會 / 新北市',
    summary: '暑假檔期的全國錦標賽，已於 8 月 14 至 16 日在三峽國民運動中心圓滿落幕，賽前並加開組別因應報名熱潮。完整成績可於總會官網查詢。',
    categories: ['公開組', '分齡組', '雙打組'],
    registrationDeadline: '2026-07-15',
    registrationUrl: 'https://www.ctpf.org.tw/ctpf/news/zh-tw/detailed/2198',
    officialUrl: 'https://www.ctpf.org.tw/',
  },
  {
    id: 'tmlp-kaohsiung-2026',
    name: '2026 TMLP 台灣匹克球積分巡迴賽 高雄站',
    nameEn: 'TMLP Taiwan Pickleball Tour - Kaohsiung 2026',
    date: '2026-04-11',
    endDate: '2026-04-12',
    dateLabel: '2026 年 4 月 11 - 12 日',
    venue: '高雄市鼓山高中',
    city: '高雄市',
    region: '南部',
    level: '區域',
    status: 'completed',
    organizer: 'TMLP 台灣匹克球積分巡迴賽',
    summary: 'TMLP 積分巡迴賽 2026 賽季首站，4 月 11、12 日於高雄鼓山高中舉行。全站採男雙／女雙／混雙三項，成績計入 TMLP 年度積分。',
    categories: ['男子雙打', '女子雙打', '混合雙打'],
    officialUrl: 'https://www.tmlp.com.tw/events',
  },
  {
    id: 'tmlp-chiayi-2026',
    name: '2026 TMLP 台灣匹克球積分巡迴賽 嘉義站',
    nameEn: 'TMLP Taiwan Pickleball Tour - Chiayi 2026',
    date: '2026-05-30',
    endDate: '2026-05-31',
    dateLabel: '2026 年 5 月 30 - 31 日',
    venue: '嘉義市國民運動中心',
    city: '嘉義市',
    region: '南部',
    level: '區域',
    status: 'completed',
    organizer: 'TMLP 台灣匹克球積分巡迴賽',
    summary: 'TMLP 積分巡迴賽第 2 站，於嘉義市國民運動中心舉行。除積分外另提供紀念品與補給，成績同步計入 DUPR。',
    categories: ['男子雙打', '女子雙打', '混合雙打'],
    officialUrl: 'https://www.tmlp.com.tw/events',
  },
  {
    id: 'tmlp-miaoli-2026',
    name: '2026 TMLP 台灣匹克球積分巡迴賽 苗栗站',
    nameEn: 'TMLP Taiwan Pickleball Tour - Miaoli 2026',
    date: '2026-06-27',
    endDate: '2026-06-28',
    dateLabel: '2026 年 6 月 27 - 28 日',
    venue: '竹南運動公園籃球場（苗栗縣竹南鎮新南里公園路55號）',
    city: '苗栗縣',
    region: '中部',
    level: '區域',
    status: 'completed',
    organizer: 'TMLP 台灣匹克球積分巡迴賽',
    summary: 'TMLP 積分巡迴賽第 3 站，於苗栗竹南運動公園舉行。苗栗縣目前沒有匹克球專用場館，這站是當地少見的全國級賽事。',
    categories: ['男子雙打', '女子雙打', '混合雙打'],
    officialUrl: 'https://www.tmlp.com.tw/events',
  },
  {
    id: 'tmlp-hsinchu-2026',
    name: '2026 TMLP 台灣匹克球積分巡迴賽 新竹站',
    nameEn: 'TMLP Taiwan Pickleball Tour - Hsinchu 2026',
    date: '2026-08-15',
    endDate: '2026-08-16',
    dateLabel: '2026 年 8 月 15 - 16 日',
    venue: '王爺壟運動公園（新竹縣湖口鄉信義村中山路一段700號）',
    city: '新竹縣',
    region: '北部',
    level: '區域',
    status: 'completed',
    organizer: 'TMLP 台灣匹克球積分巡迴賽',
    summary: 'TMLP 積分巡迴賽 2026 下半年首站，已於 8 月 15、16 日在湖口王爺壟運動公園完賽。巡迴賽採積分制，10 月新北站續戰、年終於台北舉行積分邀請賽。',
    categories: ['公開組', '積分組'],
    registrationUrl: 'https://www.tmlp.com.tw/events',
    officialUrl: 'https://www.tmlp.com.tw/',
  },
  {
    id: 'hsinchu-mayor-cup-2026',
    name: '115年度新竹縣長盃匹克球錦標賽',
    nameEn: 'Hsinchu County Mayor Cup Pickleball Championships 2026',
    date: '2026-08-29',
    endDate: '2026-08-30',
    dateLabel: '2026 年 8 月 29 - 30 日',
    venue: '竹北國民運動中心',
    city: '新竹縣',
    region: '北部',
    level: '區域',
    status: 'completed',
    organizer: '新竹縣政府 / 中華民國匹克球總會',
    summary: '新竹縣年度縣長盃錦標賽，已於 8 月 29、30 日在竹北國民運動中心圓滿落幕，是大新竹地區匹克球熱潮下的指標賽事。',
    categories: ['公開組', '分齡組'],
    officialUrl: 'https://www.ctpf.org.tw/',
  },
  {
    id: 'kavalan-cup-2026',
    name: '2026 噶瑪蘭盃匹克球錦標賽',
    nameEn: 'Kavalan Cup Pickleball Championships 2026',
    date: '2026-08-15',
    dateLabel: '2026 年 8 月（尚待總會確認）',
    venue: '宜蘭運動公園',
    city: '宜蘭縣',
    region: '東部',
    level: '全國',
    status: 'completed',
    organizer: '宜蘭縣政府 / 中華民國匹克球協會',
    summary: '位於宜蘭運動公園的夏季盛事。註：總會 7 月最新行事曆暫未列出本賽事，日期與是否舉辦請以官方公告為準。',
    categories: ['公開組', '業餘組', '親子組'],
    officialUrl: 'https://pickleball.org.tw/',
  },
  {
    id: 'taipei-open-2026',
    name: '2026 臺北匹克球公開賽',
    nameEn: 'Taipei Pickleball Open 2026',
    date: '2026-11-01',
    dateLabel: '2026 年 11 月（日期與地點待定）',
    venue: '待公告',
    city: '台北市',
    region: '北部',
    level: '全國',
    status: 'upcoming',
    organizer: '臺北市政府體育局 / 中華民國匹克球總會',
    summary: '2025 年使用 17 面天母新場地圓滿落幕。依總會 7 月最新行事曆，2026 年賽事移至 11 月舉行，確切日期與地點待官方公告。',
    categories: ['公開組', '分齡組', '混合雙打'],
    officialUrl: 'https://www.ctpf.org.tw/',
    featured: true,
  },
  {
    id: 'taiwan-womens-open-2026',
    name: '2026 臺灣女子匹克球公開賽',
    nameEn: 'Taiwan Women\'s Pickleball Open 2026',
    date: '2026-10-01',
    dateLabel: '2026 年 10 月（日期待定）',
    venue: '待公告',
    city: '台灣',
    region: '全國',
    level: '全國',
    status: 'upcoming',
    organizer: '中華民國匹克球總會',
    summary: '全台首個以女子選手為主體的全國公開賽，列於總會 2026 年行事曆 10 月檔期。截至 10 月 5 日官方仍未公告確切日期與地點，本站持續追蹤。',
    categories: ['女子單打', '女子雙打'],
    officialUrl: 'https://www.ctpf.org.tw/',
  },
  {
    id: 'tmlp-newtaipei-2026',
    name: '2026 TMLP 台灣匹克球積分巡迴賽 新北站',
    nameEn: 'TMLP Taiwan Pickleball Tour - New Taipei 2026',
    date: '2026-10-17',
    endDate: '2026-10-18',
    dateLabel: '2026 年 10 月 17 - 18 日',
    venue: '新北市林口高中（新北市林口區東勢里仁愛路二段173號）',
    city: '新北市',
    region: '北部',
    level: '區域',
    status: 'registration',
    organizer: 'TMLP 台灣匹克球積分巡迴賽',
    summary: 'TMLP 積分巡迴賽 2026 賽季的公開報名最終戰，10 月 17、18 日於新北市林口高中舉行，兩天報名逾千人。採男雙／女雙／混雙三項，成績計入 TMLP 年度積分，年終站改為依積分邀請參賽。',
    categories: ['公開組', '積分組'],
    registrationUrl: 'https://www.tmlp.com.tw/events',
    officialUrl: 'https://www.tmlp.com.tw/',
  },
  {
    id: 'tmlp-taipei-final-2026',
    name: '2026 TMLP 台北站－年終積分邀請賽',
    nameEn: 'TMLP Taipei Year-End Invitational 2026',
    date: '2026-12-12',
    dateLabel: '2026 年 12 月 12 日',
    venue: 'Triple Ace & P.DANG 匹克球館（新北市淡水區）',
    city: '新北市',
    region: '北部',
    level: '區域',
    status: 'upcoming',
    organizer: 'TMLP 台灣匹克球積分巡迴賽',
    summary: 'TMLP 巡迴賽年終壓軸，依全年積分邀請選手參加的年終邀請賽。官方賽事頁標示 12 月 12 日於淡水 Triple Ace & P.DANG 球館舉行（同頁仍殘留舊日期 11/22，以賽事資訊欄為準）。',
    officialUrl: 'https://www.tmlp.com.tw/events/taipei-season-invitational-2026',
    categories: ['積分邀請組'],
  },
  {
    id: 'sports-day-2026',
    name: '115年國民體育日匹克球系列推廣活動',
    nameEn: 'National Sports Day Pickleball Promotion 2026',
    date: '2026-09-06',
    endDate: '2026-09-13',
    dateLabel: '2026 年 9 月 6 日（身心障礙推廣）、9 月 13 日（屏商校區推廣）',
    venue: '國立屏東大學（屏商校區）等',
    city: '屏東縣',
    region: '南部',
    level: '公益',
    status: 'completed',
    organizer: '運動部全民運動署 / 屏東大學',
    summary: '國民體育日官方系列推廣活動：9/6 身心障礙匹克球推廣、9/12 屏東縣全民邀請賽、9/13 屏商校區推廣活動。以免費體驗與表演賽讓民眾認識匹克球，適合家庭與初學者。',
    categories: ['體驗活動', '表演賽', '親子組', '身心障礙組'],
  },
  {
    id: 'mr-brown-open-2026',
    name: '2026 伯朗盃匹克球公開賽',
    nameEn: 'Mr. Brown Pickleball Open 2026',
    date: '2026-11-07',
    dateLabel: '2026 年 11 月（日期待確認）',
    venue: '宜蘭縣羅東運動公園',
    city: '宜蘭縣',
    region: '東部',
    level: '全國',
    status: 'upcoming',
    organizer: '金車伯朗 / 中華民國匹克球協會',
    summary: '企業冠名贊助的年度指標賽事，獎金與規格皆具話題性。適合中高階選手爭取年度最佳賽事積分。',
    categories: ['公開組', '業餘組', '青少年組'],
  },
  {
    id: 'star-cup-2026',
    name: '2026 星動盃明星匹克球公益賽',
    nameEn: 'Star Cup Celebrity Pickleball Charity 2026',
    date: '2026-12-06',
    dateLabel: '2026 年 12 月 6 日',
    venue: '臺北體育館',
    city: '台北市',
    region: '北部',
    level: '公益',
    status: 'upcoming',
    organizer: '中華民國匹克球總會',
    summary: '2025 年首屆即獲廣泛迴響的公益賽事，多位藝人與球友同場競技，所得捐贈公益團體。2026 年規模擴大。',
    categories: ['明星組', '公益挑戰組', '媒體組'],
    featured: true,
  },
  {
    id: 'apg-2026',
    name: '2026 亞洲匹克球運動會 (APG)',
    nameEn: 'Asia Pickleball Games 2026',
    date: '2026-12-31',
    dateLabel: '2026 年（AFP 尚未公告日期與地點）',
    venue: '待公告',
    city: '亞洲輪值',
    region: '國際',
    level: '國際',
    status: 'upcoming',
    organizer: 'Asia Federation of Pickleball (AFP)',
    summary: '亞洲匹克球聯合會主辦的區域最高等級賽事。2023、2024 年於台中舉辦，2025 年 12 月於東京落幕；截至 2026 年 7 月，AFP 尚未公告 2026 屆的日期與地點，官網目前僅列 AOPC 2026 與 APJN/APUN 2026（泰國）。',
    categories: ['國家代表隊', '公開組', '職業組'],
    officialUrl: 'https://www.afpickleball.org/',
    featured: true,
  },
  {
    id: 'inclusive-cup-2026',
    name: '115年全國共融盃匹克球錦標賽',
    nameEn: 'National Inclusive Cup Pickleball Championships 2026',
    date: '2026-12-10',
    endDate: '2026-12-13',
    dateLabel: '2026 年 12 月 10 - 13 日',
    venue: '新北市八里',
    city: '新北市',
    region: '北部',
    level: '全國',
    status: 'upcoming',
    organizer: '中華民國匹克球總會',
    summary: '年末壓軸的全國共融盃，強調全齡與身心障礙選手共同參與的共融精神，於新北市八里舉行。',
    categories: ['公開組', '共融組', '身心障礙組'],
    officialUrl: 'https://www.ctpf.org.tw/',
  },
  {
    id: 'nanhua-cup-2026',
    name: '2026 南華盃－青銀共融匹克球全國聯賽',
    nameEn: 'Nanhua Cup Intergenerational Pickleball National League 2026',
    date: '2026-09-12',
    endDate: '2026-11-15',
    dateLabel: '2026 年 9 月 12 日 - 11 月 15 日',
    venue: '南華大學',
    city: '嘉義縣',
    region: '南部',
    level: '全國',
    status: 'ongoing',
    organizer: '中華民國匹克球總會 / 南華大學',
    summary: '為期兩個月的青銀共融全國聯賽，於南華大學舉行，鼓勵青年與銀髮世代同場競技交流，是 CTPF 下半年推廣全齡運動的重點賽事。',
    categories: ['青銀共融組', '公開組'],
    officialUrl: 'https://www.ctpf.org.tw/',
  },
  {
    id: 'community-league-2026',
    name: '115年全國匹克球社區運動聯賽',
    nameEn: 'National Community Pickleball League 2026',
    date: '2026-10-01',
    endDate: '2026-11-08',
    dateLabel: '2026 年 10 月分區預賽・11 月 7 - 8 日總決賽',
    venue: '北中南東分區預賽／總決賽於國立宜蘭大學體育館',
    city: '全台',
    region: '全台',
    level: '全國',
    status: 'upcoming',
    organizer: '中華民國匹克球總會',
    summary: '以社區為單位的全國聯賽，10 月於北、中、南、東四區進行預賽，11 月 7-8 日在國立宜蘭大學體育館舉行總決賽，深入基層推廣匹克球。',
    categories: ['社區組'],
    officialUrl: 'https://www.ctpf.org.tw/',
  },
  {
    id: 'taichung-international-open-2026',
    name: '2026 台中國際匹克球公開賽',
    nameEn: 'Taichung International Pickleball Open 2026',
    date: '2026-10-14',
    endDate: '2026-10-17',
    dateLabel: '2026 年 10 月 14 - 17 日',
    venue: '台中國際網球中心',
    city: '台中市',
    region: '中部',
    level: '國際',
    status: 'upcoming',
    organizer: '台中市政府運動局 / 中華民國匹克球協會 / 台中市匹克球協會',
    summary: '獲運動部「台灣品牌國際賽事計畫」補助的國際賽，10 月 14 日開幕，開幕報導稱 16 國、逾千名選手參賽（美、加、日、韓、新加坡、荷蘭、菲律賓、印尼、馬來西亞等），賽事於中華民國匹克球協會 YouTube 轉播；各組完整成績以主辦單位公告為準。賽前預估 10 國約 1,300 人，四天賽程涵蓋團體賽、單打、雙打與混雙，總獎金 43,500 美元。10 月 14 日團體賽導入美國匹克球小聯盟（MiLP）賽制，各組冠軍取得 2027 年美國小聯盟賽外卡。賽期同步舉辦「亞洲匹克球運動產業趨勢論壇」，玖壹壹洋蔥出席宣傳。報名已於 9 月 26 日截止。',
    categories: ['團體賽（MiLP 賽制）', '單打', '雙打', '混合雙打'],
    registrationDeadline: '2026-09-26',
    prizeInfo: '總獎金 43,500 美元；團體賽各組冠軍獲 2027 美國小聯盟賽外卡',
    officialUrl: 'https://udn.com/news/story/7241/9765281',
    featured: true,
  },
  {
    id: 'shin-kong-cup-2026',
    name: '2026 新光認真盃匹克球公開賽',
    nameEn: 'Shin Kong Life Pickleball Open 2026',
    date: '2026-11-14',
    endDate: '2026-11-15',
    dateLabel: '2026 年 11 月 14 - 15 日',
    venue: '國立臺北教育大學',
    city: '台北市',
    region: '北部',
    level: '公益',
    status: 'registration',
    organizer: '新光人壽',
    summary: '新光人壽首屆主辦的全民公開賽，依 DUPR 分級設新手組、初階組、中階組，各級皆有男雙、女雙、混雙，預計招募約 216 人（108 組雙打）。報名至 10 月 18 日。',
    categories: ['新手組', '初階組', '中階組（各組皆含男雙／女雙／混雙）'],
    registrationUrl: 'https://bao-ming.com/eb/content/7211',
    registrationDeadline: '2026-10-18',
    officialUrl: 'https://sports.ltn.com.tw/news/breakingnews/5591005',
  },
  {
    id: 'tci-app-asia-tour-2026',
    name: 'TCI APP ASIA TOUR 台北站',
    nameEn: 'TCI APP Asia Tour Taipei 2026',
    date: '2026-11-12',
    dateLabel: '2026 年 11 月（確切日期待 APP 官方公告）',
    venue: '台北市（場館待官方公告）',
    city: '台北市',
    region: '北部',
    level: '國際',
    status: 'upcoming',
    organizer: 'APP Asia Tour / 中華民國匹克球總會',
    summary: '亞太職業巡迴賽事登陸台北，APP 官方目前只公布舉辦月份為 11 月，確切日期、場館與組別尚未公告。是台灣選手在主場接軌國際積分賽的重要機會。',
    officialUrl: 'https://theapp.global/news/preview-penang-open-2026',
    categories: ['職業組', '公開組'],
  },
  {
    id: 'university-championships-2026',
    name: '大專校院115學年度匹克球錦標賽',
    nameEn: 'National University Pickleball Championships 2026',
    date: '2026-11-19',
    dateLabel: '115 學年度（日期待大專體總公告）',
    venue: '待公告（113、114 學年度皆在樹德科技大學舉行）',
    city: '高雄市',
    region: '南部',
    level: '全國',
    status: 'upcoming',
    organizer: '中華民國大專院校體育總會 / 中華民國匹克球總會',
    summary: '大專校院匹克球錦標賽自 113 學年度起舉辦，113、114 學年度都在高雄樹德科大進行。115 學年度的競賽規程與日期尚未公告，公布後本站更新。',
    officialUrl: 'https://pe.nfu.edu.tw/wp-content/uploads/sites/22/2025/08/「中華民國大專校院114學年度匹克球錦標賽」競賽規程.pdf',
    categories: ['大專男子組', '大專女子組', '混合組'],
  },
  {
    id: 'city-cup-2026',
    name: '2026 城市盃全國匹克球團體錦標賽',
    nameEn: 'City Cup National Pickleball Team Championships 2026',
    date: '2026-11-14',
    endDate: '2026-11-15',
    dateLabel: '2026 年 11 月 14 - 15 日',
    venue: '靜宜大學體育館（台中市沙鹿區）',
    city: '台中市',
    region: '中部',
    level: '全國',
    status: 'registration',
    organizer: '運動部全民運動署（協辦：中華民國匹克球協會、台中市運動局、台中市匹克球協會）',
    summary: '以縣市為單位組隊的全國團體錦標賽，由運動部全民運動署主辦，11 月 14、15 日在台中靜宜大學體育館舉行。報名已於 10 月 25 日截止。',
    categories: ['縣市團體賽'],
    registrationUrl: 'https://vicvoy.com/tournament/detail/442',
    registrationDeadline: '2026-10-25',
    officialUrl: 'https://vicvoy.com/tournament/detail/442',
  },
  {
    id: 'tonghua-cup-2026',
    name: '115 年新北市桐花盃第二屆匹克球錦標賽',
    nameEn: 'New Taipei Tung Blossom Cup Pickleball Championships 2026',
    date: '2026-11-21',
    endDate: '2026-11-22',
    dateLabel: '2026 年 11 月 21 - 22 日',
    venue: '土城國民運動中心 3F',
    city: '新北市',
    region: '北部',
    level: '區域',
    status: 'registration',
    organizer: '土城國民運動中心（協辦：中華民國匹克球協會、新北市匹克球協會）',
    summary: '新北土城國民運動中心主辦的第二屆桐花盃，11 月 21、22 日在中心 3 樓舉行，報名至 11 月 8 日中午 12 點。',
    categories: ['雙打各組（依官方報名頁）'],
    registrationUrl: 'https://vicvoy.com/tournament/detail/399',
    registrationDeadline: '2026-11-08',
    officialUrl: 'https://vicvoy.com/tournament/detail/399',
  },
  {
    id: 'kaohsiung-student-2026',
    name: '115 年第一屆高雄匹克球發展協會全國學生匹克球錦標賽',
    nameEn: 'Kaohsiung National Student Pickleball Championships 2026',
    date: '2026-11-21',
    endDate: '2026-11-22',
    dateLabel: '2026 年 11 月 21 - 22 日',
    venue: '高雄市立鼓山高中風雨球場',
    city: '高雄市',
    region: '南部',
    level: '全國',
    status: 'upcoming',
    organizer: '高雄匹克球發展協會',
    summary: '高雄匹克球發展協會首度舉辦的全國學生錦標賽，11 月 21、22 日在鼓山高中風雨球場進行，報名方式與截止日見官方報名頁。',
    categories: ['學生組（依官方報名頁）'],
    registrationUrl: 'https://www.beclass.com/rid=30528896abe429c0ce5c',
    officialUrl: 'https://www.beclass.com/rid=30528896abe429c0ce5c',
  },
  {
    id: 'pickle-love-2026',
    name: '2026 讓礙同行 Pickle Love 匹克球公益賽',
    nameEn: 'Pickle Love Charity Pickleball Tournament 2026',
    date: '2026-12-05',
    dateLabel: '2026 年 12 月 5 日',
    venue: '麗寶 Outlet Mall NAPA 戶外球場（台中市后里區）',
    city: '台中市',
    region: '中部',
    level: '公益',
    status: 'registration',
    organizer: '浪礙長青公益協會（指導：中華民國匹克球協會）',
    summary: '浪礙長青公益協會主辦的公益賽，12 月 5 日在后里麗寶 Outlet 的 NAPA 戶外球場舉行，報名至 11 月 15 日下午 5 點。',
    categories: ['公益組（依官方報名頁）'],
    registrationUrl: 'https://vicvoy.com/tournament/detail/432',
    registrationDeadline: '2026-11-15',
    officialUrl: 'https://vicvoy.com/tournament/detail/432',
  },
];

// 狀態依日期自動推算，避免手動維護的 status 過期（以台灣日期為準）。
// 只套用在日期已確定的賽事：dateLabel 含「待」（待定／待公告／待確認）的維持手動狀態。
const todayInTaiwan = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Taipei' });

const withLiveStatus = (t: Tournament, today: string): Tournament => {
  if (t.dateLabel.includes('待')) return t;
  const end = t.endDate ?? t.date;
  if (end < today) return { ...t, status: 'completed' };
  if (t.date <= today) return { ...t, status: 'ongoing' };
  if (t.status === 'registration' && t.registrationDeadline && t.registrationDeadline < today) {
    return { ...t, status: 'upcoming' };
  }
  return t;
};

export const TOURNAMENTS_2026: Tournament[] = RAW_TOURNAMENTS.map(t => withLiveStatus(t, todayInTaiwan()));

// 取得即將到來的賽事（依照今天日期）
export const getUpcomingTournaments = (limit?: number): Tournament[] => {
  const today = todayInTaiwan();
  const upcoming = TOURNAMENTS_2026
    .filter(t => t.date >= today && t.status !== 'completed')
    .sort((a, b) => a.date.localeCompare(b.date));
  return limit ? upcoming.slice(0, limit) : upcoming;
};

// 取得重點賽事
export const getFeaturedTournaments = (): Tournament[] => {
  return TOURNAMENTS_2026.filter(t => t.featured);
};

// 依等級分組
export const getTournamentsByLevel = (level: TournamentLevel): Tournament[] => {
  return TOURNAMENTS_2026.filter(t => t.level === level);
};

// 2026 年台灣匹克球生態統計（來源：CTPF、遠見雜誌報導）
export const TAIWAN_PICKLEBALL_STATS_2026 = {
  estimatedPlayers: 1_200_000,    // 預估 2026 年球友人數（遠見：2024=14萬, 2025=50萬, 2026=120萬）
  certifiedCoaches: 1400,          // CTPF 已認證 C 級教練
  knownCourts: 213,                // 全台已知球場（本站收錄 213 座，持續新增）
  tournamentsYear: TOURNAMENTS_2026.length,
  governingBody: '中華民國匹克球協會 (CTPF)',
  chairman: '陳朝鍵',
  lastUpdated: '2026-11-05',
};
